import { ChangeDetectionStrategy, Component } from '@angular/core';
import { AbstractControl, FormBuilder, Validators } from '@angular/forms';
import { FlightSearchStore } from './flight-search.store';
import { combineLatest, map, Observable, tap } from 'rxjs';
import { RequestState, RequestStatus } from '@shared/models/request-state';
import { DateTime } from 'luxon';
import { FlightList, FlightListItem } from './models/flight-list';
import { Router } from '@angular/router';
import { ConnectionStatusService } from '@shared/services/connection-status.service';
import { AlertService } from '@shared/services/alert/alert.service';

@Component({
  selector: 'app-flight-search',
  templateUrl: './flight-search.component.html',
  styleUrl: './flight-search.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlightSearchComponent {
  constructor(
    private flightSearchStore: FlightSearchStore,
    private router: Router,
    private connectionStatusService: ConnectionStatusService,
    private alertService: AlertService,
  ) {}

  routeSearch$?: Observable<RequestState<FlightList>>;
  connectionStatus$ = this.connectionStatusService.connectionStatus$.pipe(
    tap((status) => {
      this.connectionStatus = status;
    }),
  );
  connectionStatus?: 'online' | 'offline';

  routeForm = new FormBuilder().group({
    from: ['', [Validators.required, Validators.minLength(3)]],
    to: ['', [Validators.required, Validators.minLength(3)]],
    date: [new Date(), Validators.required],
  });

  seqForm = new FormBuilder().group({
    seqNum: ['', [Validators.required, Validators.minLength(3)]],
    position: ['CA', [Validators.required, Validators.minLength(2)]],
    date: [new Date(), Validators.required],
  });

  capitalize(control: AbstractControl): void {
    control.setValue(
      (control.value as string).toUpperCase().replace(/[^A-Z]/, ''),
    );
  }

  numeric(control: AbstractControl): void {
    control.setValue((control.value as string).replace(/\D/, ''));
  }

  onRouteSubmit(): void {
    const { from, to, date } = this.routeForm.controls;

    this.routeSearch$ = combineLatest([
      this.flightSearchStore.getRouteResults(
        from.value || '',
        to.value || '',
        DateTime.fromJSDate(date.value as Date),
      ),
      this.flightSearchStore.savedFlights$,
    ]).pipe(
      map(([flightList, savedFlights]) => {
        if (this.connectionStatus === 'offline') {
          this.showOfflineMsg();

          return new RequestState<FlightList>(
            RequestStatus.IsCompleteNoResponse,
          );
        }
        // Set isSaved if necessary.
        if (flightList.response) {
          for (const flight of flightList.response.flights) {
            flight.isSaved = false;
            for (const savedFlight of savedFlights) {
              if (flight.id === savedFlight.id) {
                flight.isSaved = true;
              }
            }
          }
        }
        return flightList;
      }),
    );
  }

  // Swap dep/arr airports.
  onSwapClick(): void {
    const from = this.routeForm.controls.from.value;
    const to = this.routeForm.controls.to.value;
    this.routeForm.controls.from.setValue(to);
    this.routeForm.controls.to.setValue(from);
  }

  onSeqSubmit(): void {
    if (this.connectionStatus === 'offline') {
      this.showOfflineMsg();
    } else {
      const { seqNum, position, date } = this.seqForm.controls;
      if (seqNum.value && position.value && date.value) {
        const sequenceUrl = this.createSeqUrl(
          seqNum.value,
          position.value,
          DateTime.fromJSDate(date.value as Date).toISODate(),
        );
        this.router.navigate([sequenceUrl]);
      }
    }
  }

  onFlightClicked(flightList: FlightList, flight: FlightListItem): void {
    if (this.connectionStatus === 'offline') {
      this.showOfflineMsg();
    } else {
      const flightNum = flight.flightNumber;
      const dep = flightList.depStation;
      const arr = flightList.arrStation;
      const date = flightList.date.toISODate();

      this.router.navigate(['lookup-sequence', flightNum, dep, arr, date]);
    }
  }

  onStarClick(
    id: string,
    flightList: FlightList,
    flight: FlightListItem,
  ): void {
    if (this.connectionStatus === 'offline') {
      this.showOfflineMsg();
    } else {
      // Toggle saved state.
      this.flightSearchStore.toggleSavedFlight(id, flightList, flight);
    }
  }

  private createSeqUrl(seqNum: string, seat: string, seqDate: string) {
    return `sequence/${seqNum}/${seat}/${seqDate}`;
  }

  private showOfflineMsg(): void {
    this.alertService.setAlert({
      type: 'warning',
      message: 'Not available in offline mode.',
    });
  }
}
